"use client";

import { ReactNodeViewRenderer } from "@tiptap/react";
import { mergeAttributes, Range } from "@tiptap/core";

import { VideoBlockView } from "./components/VideoBlockView.js";
import { Video } from "../Video/Video.js";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    videoBlock: {
      setVideoBlock: (attributes: {
        src: string;
        poster: string;
        assetId: string;
        playlistUrl?: string;
        title: string;
        actualWidth?: number;
        actualHeight?: number;
      }) => ReturnType;
      setVideoBlockAt: (attributes: {
        src: string;
        poster: string;
        assetId: string;
        playlistUrl?: string;
        title: string;
        pos: number | Range;
        actualWidth?: number;
        actualHeight?: number;
      }) => ReturnType;
      setVideoBlockAlign: (align: "left" | "center" | "right") => ReturnType;
      setVideoBlockWidth: (width: number) => ReturnType;
      setVideoBlockCaption: (caption: boolean) => ReturnType;
    };
  }
}

export const VideoBlock = Video.extend({
  name: "videoBlock",

  group: "block",

  defining: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: "",
        parseHTML: (element) => element.getAttribute("src"),
        renderHTML: (attributes) => ({
          src: attributes.src,
        }),
      },
      title: {
        default: "",
        parseHTML: (element) => element.getAttribute("title"),
        renderHTML: (attributes) => ({
          title: attributes.title,
        }),
      },
      poster: {
        default: "",
        parseHTML: (element) => element.getAttribute("poster"),
        renderHTML: (attributes) => ({
          poster: attributes.poster,
        }),
      },
      playlistUrl: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-playlist-url"),
        renderHTML: (attributes) => ({
          "data-playlist-url": attributes.playlistUrl,
        }),
      },
      width: {
        default: "100%",
        parseHTML: (element) => element.getAttribute("data-width"),
        renderHTML: (attributes) => ({
          "data-width": attributes.width,
        }),
      },
      actualWidth: {
        default: 0,
        parseHTML: (element) => element.getAttribute("data-actual-width"),
        renderHTML: (attributes) => ({
          "data-actual-width": attributes.actualWidth,
        }),
      },
      actualHeight: {
        default: 0,
        parseHTML: (element) => element.getAttribute("data-actual-height"),
        renderHTML: (attributes) => ({
          "data-actual-height": attributes.actualHeight,
        }),
      },
      align: {
        default: "center",
        parseHTML: (element) => element.getAttribute("data-align"),
        renderHTML: (attributes) => ({
          "data-align": attributes.align,
        }),
      },
      alt: {
        default: undefined,
        parseHTML: (element) => element.getAttribute("alt"),
        renderHTML: (attributes) => ({
          alt: attributes.alt,
        }),
      },
      caption: {
        default: true,
        parseHTML: (element) => element.getAttribute("data-caption") === "true",
        renderHTML: (attributes) => ({
          "data-caption": attributes.caption,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'video[src*="tiptap.dev"]:not([src^="data:"]), video[src*="windows.net"]:not([src^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      { class: "imageBlock" },
      ["video", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)],
      [
        "caption",
        mergeAttributes(
          {
            class:
              "text-center text-gray-400 w-full h-16 mb-2 placeholder-black p-1 text-sm  border-0 rounded appearance-none",
          },
          { defaultValue: HTMLAttributes.title || HTMLAttributes.caption }
        ),
        HTMLAttributes.title || HTMLAttributes.caption,
      ],
    ];
  },

  addCommands() {
    return {
      setVideoBlock:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContent({
            type: "videoBlock",
            attrs: {
              src: attrs.src,
              poster: attrs.poster,
              assetId: attrs.assetId,
              playlistUrl: attrs.playlistUrl,
              title: attrs.title,
              actualWidth: attrs.actualWidth,
              actualHeight: attrs.actualHeight,
            },
          });
        },

      setVideoBlockAt:
        (attrs) =>
        ({ commands }) => {
          return commands.insertContentAt(attrs.pos, {
            type: "videoBlock",
            attrs: {
              src: attrs.src,
              poster: attrs.poster,
              assetId: attrs.assetId,
              playlistUrl: attrs.playlistUrl,
              title: attrs.title,
              actualWidth: attrs.actualWidth,
              actualHeight: attrs.actualHeight,
            },
          });
        },

      setVideoBlockAlign:
        (align) =>
        ({ commands }) =>
          commands.updateAttributes("videoBlock", { align }),

      setVideoBlockWidth:
        (width) =>
        ({ commands }) =>
          commands.updateAttributes("videoBlock", {
            width: `${Math.max(0, Math.min(100, width))}%`,
          }),

      setVideoBlockCaption:
        (caption) =>
        ({ commands }) =>
          commands.updateAttributes("videoBlock", { caption }),
    };
  },

  addNodeView() {
    //@ts-ignore
    return ReactNodeViewRenderer(VideoBlockView);
  },
});

export default VideoBlock;
